/**
 * eStream Browser SDK Client
 *
 * High-level client for browser applications.
 * Wraps @estream/transport with convenient APIs.
 */
import { EstreamTransport } from '@estream/transport';
/**
 * eStream client for browser applications
 *
 * @example
 * ```typescript
 * const client = new EstreamClient({
 *   url: 'https://edge.estream.dev:4433',
 *   network: 'alpha-devnet',
 * });
 *
 * await client.connect();
 *
 * // Subscribe to topology updates
 * client.subscribe('/console/topology', {
 *   onData: (nodes) => console.log('Nodes:', nodes),
 * });
 *
 * // Emit a message
 * await client.emit('/my-app/events', { type: 'click', x: 100, y: 200 });
 * ```
 */
export class EstreamClient {
    transport;
    config;
    subscriptions = new Map();
    subscriptionIds = new Map();
    constructor(config) {
        this.config = {
            network: 'alpha-devnet',
            autoConnect: false,
            debug: false,
            ...config,
        };
        this.transport = new EstreamTransport({
            url: this.config.url,
        });
        // Forward state changes
        this.transport.addEventListener('statechange', (e) => {
            if (this.config.debug) {
                console.log('[eStream] State:', e.detail);
            }
        });
        if (this.config.autoConnect) {
            this.connect().catch(console.error);
        }
    }
    /** Current connection state */
    get state() {
        return this.transport.state;
    }
    /** Whether connected to edge node */
    get isConnected() {
        return this.transport.isConnected;
    }
    /** Current network */
    get network() {
        return this.config.network;
    }
    /**
     * Connect to the edge node
     */
    async connect() {
        if (this.config.debug) {
            console.log('[eStream] Connecting to', this.config.url);
        }
        await this.transport.connect();
    }
    /**
     * Disconnect from the edge node
     */
    async disconnect() {
        await this.transport.disconnect();
    }
    /**
     * Subscribe to a lattice topic
     *
     * @param topic - Lattice path (e.g., /console/topology)
     * @param options - Subscription options
     * @returns Unsubscribe function
     *
     * @example
     * ```typescript
     * const unsub = client.subscribe('/console/topology', {
     *   onData: (data) => setNodes(data.nodes),
     * });
     *
     * // Later...
     * unsub();
     * ```
     */
    subscribe(topic, options = {}) {
        const { transform, onData, onError } = options;
        // Create callback wrapper
        const callback = (event) => {
            try {
                const data = transform
                    ? transform(event)
                    : this.decodePayload(event.data);
                onData?.(data);
            }
            catch (e) {
                onError?.(e instanceof Error ? e : new Error(String(e)));
            }
        };
        // Track internal subscribers
        if (!this.subscriptions.has(topic)) {
            this.subscriptions.set(topic, new Set());
            // Create transport subscription
            this.transport.subscribe(topic, callback).then((id) => {
                this.subscriptionIds.set(topic, id);
            });
        }
        const callbacks = this.subscriptions.get(topic);
        callbacks.add(callback);
        // Return unsubscribe function
        return () => {
            callbacks.delete(callback);
            if (callbacks.size === 0) {
                const id = this.subscriptionIds.get(topic);
                if (id !== undefined) {
                    this.transport.unsubscribe(id);
                }
                this.subscriptions.delete(topic);
                this.subscriptionIds.delete(topic);
            }
        };
    }
    /**
     * Emit data to a lattice topic
     *
     * @param topic - Lattice path
     * @param data - Data to emit (will be JSON-encoded)
     */
    async emit(topic, data) {
        const payload = this.encodePayload(data);
        await this.transport.emit(topic, payload);
    }
    /**
     * Subscribe and get the latest value as a Promise
     *
     * Useful for one-time fetches or initial data loading.
     */
    async get(topic) {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                unsub();
                reject(new Error(`Timeout waiting for ${topic}`));
            }, 10000);
            const unsub = this.subscribe(topic, {
                onData: (data) => {
                    clearTimeout(timeout);
                    unsub();
                    resolve(data);
                },
                onError: (error) => {
                    clearTimeout(timeout);
                    unsub();
                    reject(error);
                },
            });
        });
    }
    /**
     * Wait for connection to be established
     */
    async waitForConnection(timeoutMs = 10000) {
        if (this.isConnected)
            return;
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Connection timeout'));
            }, timeoutMs);
            const handler = (e) => {
                if (e.detail === 'connected') {
                    clearTimeout(timeout);
                    this.transport.removeEventListener('statechange', handler);
                    resolve();
                }
            };
            this.transport.addEventListener('statechange', handler);
        });
    }
    /**
     * Set session token from Spark authentication
     *
     * @param token - 32-byte session token (hex string or Uint8Array)
     */
    setSessionToken(token) {
        let tokenBytes;
        if (typeof token === 'string') {
            // Convert hex string to Uint8Array
            const hex = token.replace(/^0x/, '');
            tokenBytes = new Uint8Array(hex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        }
        else {
            tokenBytes = token;
        }
        if (tokenBytes.length !== 32) {
            throw new Error('Session token must be 32 bytes');
        }
        this.transport.setSessionToken(tokenBytes);
    }
    /**
     * Listen for state changes
     */
    onStateChange(callback) {
        const handler = (e) => {
            callback(e.detail);
        };
        this.transport.addEventListener('statechange', handler);
        return () => this.transport.removeEventListener('statechange', handler);
    }
    /**
     * Listen for session expiration
     */
    onSessionExpired(callback) {
        const handler = () => callback();
        this.transport.addEventListener('sessionexpired', handler);
        return () => this.transport.removeEventListener('sessionexpired', handler);
    }
    // ========================================================================
    // Private helpers
    // ========================================================================
    encodePayload(data) {
        const json = JSON.stringify(data);
        return new TextEncoder().encode(json);
    }
    decodePayload(payload) {
        const json = new TextDecoder().decode(payload);
        return JSON.parse(json);
    }
}
//# sourceMappingURL=client.js.map